<script lang="ts">
  import { brandStore } from '@/lib/brandStore.svelte';
  import { session } from '@/lib/session.svelte';
  import { navigateTo } from '@/lib/router';

  const labels = $derived(brandStore.labels);
  const cfg = $derived(brandStore.config);

  let previewUrl = $state<string | null>(null);
  let errorMsg = $state('');
  let fileInput: HTMLInputElement;
  let galleryInput: HTMLInputElement;

  function handleFileSelect(file: File) {
    session.setFile(file);
    previewUrl = URL.createObjectURL(file);
    errorMsg = '';
  }

  function handleCameraChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files?.[0]) handleFileSelect(input.files[0]);
  }

  function handleGalleryChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files?.[0]) handleFileSelect(input.files[0]);
  }

  function clearPreview() {
    previewUrl = null;
    session.setFile(null);
  }

  async function handleIdentify() {
    if (!session.selectedFile) return;

    const formData = new FormData();
    formData.append('image', session.selectedFile);
    if (session.vehicle) {
      formData.append('vehicle_make', session.vehicle.make);
      formData.append('vehicle_model', session.vehicle.model);
      formData.append('vehicle_year', session.vehicle.year ?? '');
    }

    try {
      errorMsg = '';
      const res = await fetch('/predict', { method: 'POST', body: formData });
      const data = await res.json();

      if (data.error) {
        errorMsg = data.error;
        return;
      }

      session.setResults({
        category: data.product_id ?? data.category ?? '',
        confidence: data.confidence ?? 0,
        scores: data.all_scores ?? {},
        matches: (data.matches ?? []).map((m: Record<string, unknown>) => {
          const vMatch = (m.vehicle_match as string) ?? '';
          const sim = (m.similarity as number) ?? 0;
          return {
            id: m.id ?? '',
            category: m.category ?? '',
            name: m.title ?? '',
            imageUrl: m.image_url,
            pricePix: m.price as number ?? 0,
            priceInstallment: m.price as number ?? 0,
            installmentCount: cfg?.pricing?.installments ?? 6,
            sourceUrl: m.link as string | undefined,
            similarity: sim,
            vehicleMatch: vMatch,
            vehicleBoost: m.vehicle_boost as number | undefined,
          };
        }),
      });
      navigateTo('results');
    } catch {
      errorMsg = labels?.errorGeneric ?? 'Erro ao processar a imagem.';
    }
  }
</script>

<div id="upload-screen" class="screen">
  {#if session.vehicle}
    <div class="vehicle-chip">
      {session.vehicle.make} {session.vehicle.model}{session.vehicle.year ? ' ' + session.vehicle.year : ''}
      <span class="close" role="button" tabindex="0" onclick={() => session.setVehicle({ make: '', model: '' })}>x</span>
    </div>
  {/if}

  <button class="btn-ar-main" id="ar-mode-btn" type="button" onclick={() => navigateTo('ar')}>
    <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
    <span class="btn-label">
      <span class="btn-title">{labels?.arModeTitle ?? 'Iniciar Scanner AR'}</span>
      <span class="btn-subtitle">{labels?.arModeSubtitle ?? 'Deteccao em Tempo Real'}</span>
    </span>
  </button>

  <button class="btn-ar-main" id="video-mode-btn" type="button" onclick={() => navigateTo('video')}>
    <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2"/>
    </svg>
    <span class="btn-label">
      <span class="btn-title">Análise de Vídeo</span>
      <span class="btn-subtitle">Processar vídeo frame a frame</span>
    </span>
  </button>

  <div class="divider">
    <span class="divider-line"></span>
    <span class="divider-text">{labels?.dividerText ?? 'ou envie uma foto'}</span>
    <span class="divider-line"></span>
  </div>

  {#if !previewUrl}
    <div class="upload-dropzone">
      <div class="upload-dropzone-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="13" r="4"/>
        </svg>
      </div>
      <p class="upload-dropzone-text">Tire ou selecione uma foto da peca</p>
      <div class="upload-actions">
        <button class="btn-upload" type="button" onclick={() => fileInput.click()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
          <span>{labels?.btnCamera ?? 'Camera'}</span>
        </button>
        <button class="btn-upload" type="button" onclick={() => galleryInput.click()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <span>{labels?.btnGallery ?? 'Galeria'}</span>
        </button>
      </div>
    </div>
  {:else}
    <div class="preview-container" id="preview-wrap">
      <img id="preview" class="preview-image" src={previewUrl} alt="Pre-visualizacao" />
      <button id="clear-btn" class="preview-clear" type="button" aria-label={labels?.labelClearPreview ?? 'Remover imagem'} onclick={clearPreview}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  {/if}

  <input type="file" bind:this={fileInput} accept="image/*" capture="environment" onchange={handleCameraChange} style="display:none" />
  <input type="file" bind:this={galleryInput} accept="image/*" onchange={handleGalleryChange} style="display:none" />

  <button class="btn btn-identify" id="identify-btn" type="button" disabled={!session.selectedFile} onclick={handleIdentify}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
    {labels?.btnIdentify ?? 'Identificar Produto'}
  </button>

  {#if errorMsg}
    <div class="error-card visible" id="error-card" role="alert">
      <svg class="error-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span id="error-msg">{errorMsg}</span>
    </div>
  {/if}
</div>
